import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { MyTasksQueryParams } from "../../../Interfaces/MyTasksInterface";

export type MyTasksQuickFilter =
  "all" | "unread" | "slaBreach" | "open" | "inProgress" | "completed";

export interface MyTasksState {
  filters: MyTasksQueryParams;
  page: number;
}

const initialFilters: MyTasksQueryParams = {
  status: undefined,
  priority: undefined,
  taskType: undefined,
  disposition: undefined,
  unread: undefined,
  slaBreach: undefined,
  assignedBy: undefined,
  search: "",
  sortBy: "lastActivityAt",
  sortOrder: "desc",
};

const initialState: MyTasksState = {
  filters: initialFilters,
  page: 1,
};

const myTasksSlice = createSlice({
  name: "myTasks",
  initialState,
  reducers: {
    setMyTasksPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setMyTasksFilter: (
      state,
      action: PayloadAction<{
        key: keyof MyTasksQueryParams;
        value: MyTasksQueryParams[keyof MyTasksQueryParams];
      }>
    ) => {
      const { key, value } = action.payload;

      if (key === "page") {
        state.page = typeof value === "number" ? value : 1;
        return;
      }

      state.filters = {
        ...state.filters,
        [key]: value,
      };
      state.page = 1;
    },
    setMyTasksFilters: (
      state,
      action: PayloadAction<Partial<MyTasksQueryParams>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 1;
    },
    resetMyTasksFilters: (state) => {
      state.filters = initialFilters;
      state.page = 1;
    },
    clearMyTasks: () => initialState,
    applyMyTasksQuickFilter: (
      state,
      action: PayloadAction<MyTasksQuickFilter>
    ) => {
      const type = action.payload;
      switch (type) {
        case "all":
          state.filters = {
            ...state.filters,
            status: undefined,
            unread: undefined,
            slaBreach: undefined,
          };
          break;
        case "unread":
          state.filters = {
            ...state.filters,
            unread: true,
            status: undefined,
            slaBreach: undefined,
          };
          break;
        case "slaBreach":
          state.filters = {
            ...state.filters,
            slaBreach: true,
            status: undefined,
            unread: undefined,
          };
          break;
        case "open":
          state.filters = {
            ...state.filters,
            status: ["OPEN"],
            unread: undefined,
            slaBreach: undefined,
          };
          break;
        case "inProgress":
          state.filters = {
            ...state.filters,
            status: ["IN_PROGRESS"],
            unread: undefined,
            slaBreach: undefined,
          };
          break;
        case "completed":
          state.filters = {
            ...state.filters,
            status: ["COMPLETED"],
            unread: undefined,
            slaBreach: undefined,
          };
          break;
      }
      state.page = 1;
    },
  },
});

export const {
  setMyTasksPage,
  setMyTasksFilter,
  setMyTasksFilters,
  resetMyTasksFilters,
  clearMyTasks,
  applyMyTasksQuickFilter,
} = myTasksSlice.actions;

export default myTasksSlice.reducer;
